import { useCallback, useRef, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  hideSingleSourceProcurementModal,
  singleSourceProcurementModalSelector,
} from '@/features/offers/slices/offerModal.slice.js'
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'

import { conditionOptions } from './conditions'
import { toast } from 'react-toastify'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import { UncontrolledFileInput } from '@/components/UncontrolledForm'

const SingleSourceProcurementModal = ({ id, type }) => {
  const { show, costBudgetId, conditions } = useSelector(singleSourceProcurementModalSelector)

  const [conditionGroupValues, setConditionGroupValues] = useState([])
  const [remark, setRemark] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dispatch = useDispatch()
  const { useApplySingleSource } = useOfferCostBudget()
  const applySingleSource = useApplySingleSource()

  useEffect(() => {
    if (Array.isArray(conditions)) {
      const otherCond = conditions.filter((cond) => cond.startsWith('other_'))

      if (otherCond.length > 0) {
        setConditionGroupValues([...conditions.filter((cond) => !cond.startsWith('other_')), 'other'])
        setRemark(otherCond[0].replace('other_', ''))
      } else {
        setConditionGroupValues(conditions)
      }
    }
  }, [conditions])

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)

      if (conditionGroupValues.includes('other') && remark === '') {
        throw new Error('Please input detailed reason !')
      }
      let formattedConditions = conditionGroupValues
      if (conditionGroupValues.includes('other')) {
        formattedConditions = conditionGroupValues.filter((cond) => cond !== 'other')
        formattedConditions.push(`other_${remark}`)
      }

      await applySingleSource.mutateAsync({
        costBudgetId,
        singleSourceProcurementConditions: formattedConditions,
      })

      //Close Model
      handleModalClose()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleModalClose = useCallback(() => {
    dispatch(hideSingleSourceProcurementModal())
  }, [dispatch])

  return (
    <Modal isOpen={show} onClose={handleModalClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="60%">
        <ModalHeader>Justification for Single Source Procurement</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap="2" direction={'column'}>
            <CheckboxGroup colorScheme="green" value={conditionGroupValues} onChange={setConditionGroupValues}>
              <VStack align="start" spacing={4}>
                {conditionOptions.map((condition, index) => (
                  <Checkbox key={condition.value} value={condition.value}>
                    {condition.description}
                  </Checkbox>
                ))}
                <Checkbox value="other">
                  <b>Others (Please Specify)</b>
                </Checkbox>
              </VStack>
            </CheckboxGroup>

            {conditionGroupValues.includes('other') && (
              <Flex width="100%">
                <Textarea
                  rows="5"
                  value={remark}
                  name="sourcing.costBudgets[0].detail.singleSourceProcurementOtherConditions"
                  onChange={(e) => setRemark(e.target.value)}
                />
              </Flex>
            )}

            <Flex>{/* <UncontrolledFileInput value={''} isMultiple /> */}</Flex>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex gap={2}>
            <Button colorScheme="blue" isLoading={isSubmitting} loadingText="Confirming" onClick={handleSubmit}>
              Save
            </Button>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              Cancel
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SingleSourceProcurementModal
